<template>
  <div class="flow-page">
    <el-card shadow="never">
      <div slot="header" class="flow-header">
        <span class="flow-title">今日流水</span>
        <el-button type="text" icon="el-icon-back" @click="goBack">返回首页</el-button>
      </div>

      <!-- 搜索 -->
      <el-form :inline="true" class="search-bar">
        <el-form-item>
          <el-input
            v-model="keyword"
            placeholder="订单号 / 手机号 / 昵称"
            clearable
            style="width: 240px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-form-item>
      </el-form>

      <!-- 订单列表 -->
      <el-table v-loading="loading" :data="list" border size="small">
        <el-table-column label="下单人" min-width="130">
          <template slot-scope="scope">
            <div>{{ scope.row.user_name }}</div>
            <div class="sub-text">{{ scope.row.user_phone || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="订单号" prop="order_no" min-width="170" show-overflow-tooltip />
        <el-table-column label="订单状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.order_status)" size="small">{{ scope.row.order_status_text }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="订单类型" width="100">
          <template slot-scope="scope">
            <el-tag size="small" :type="goodsTypeTagType(scope.row.goods_type)">
              {{ scope.row.goods_type_text }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="支付方式" prop="pay_type_text" width="100" />
        <el-table-column label="下单时间" prop="create_time" width="160" />
        <el-table-column label="支付金额" width="120" align="right">
          <template slot-scope="scope">
            <span class="amount-total">¥{{ Number(scope.row.total_amount).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="分账状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="sharingStatusTag(scope.row.profit_sharing_status)" size="small">
              {{ scope.row.profit_sharing_status_text }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="分账明细" min-width="200">
          <template slot-scope="scope">
            <div v-if="scope.row.receivers && scope.row.receivers.length" class="sharing-list">
              <div v-for="(r, idx) in scope.row.receivers" :key="idx" class="sharing-item">
                <span class="sharing-role">{{ r.role_text }}</span>
                <span class="sharing-amount">+¥{{ Number(r.amount).toFixed(2) }}</span>
              </div>
            </div>
            <div v-else class="sub-text">-</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="showDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="limit"
          :current-page.sync="page"
          @current-change="fetchList"
        />
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
      title="订单详情"
      :visible.sync="detailVisible"
      width="720px"
      top="6vh"
    >
      <div v-loading="detailLoading" class="detail-body">
        <template v-if="currentOrder">
          <el-descriptions :column="2" border size="small" class="detail-section">
            <el-descriptions-item label="订单号">{{ currentOrder.order_no }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag :type="statusTag(currentOrder.order_status)" size="small">
                {{ currentOrder.order_status_text }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="下单人">{{ currentOrder.user_name }}</el-descriptions-item>
            <el-descriptions-item label="手机号">{{ currentOrder.user_phone || '-' }}</el-descriptions-item>
            <el-descriptions-item label="订单类型">{{ currentOrder.goods_type_text }}</el-descriptions-item>
            <el-descriptions-item label="支付方式">{{ currentOrder.pay_type_text }}</el-descriptions-item>
            <el-descriptions-item label="下单时间">{{ currentOrder.create_time }}</el-descriptions-item>
            <el-descriptions-item label="商品">{{ currentOrder.product || '-' }}</el-descriptions-item>
            <el-descriptions-item label="支付金额">
              <span class="amount-total">¥{{ Number(currentOrder.total_amount).toFixed(2) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="分账状态">
              <el-tag :type="sharingStatusTag(currentOrder.profit_sharing_status)" size="small">
                {{ currentOrder.profit_sharing_status_text }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>

          <div class="detail-section-title">分账明细</div>
          <el-table :data="currentOrder.receivers" border size="small" empty-text="暂无分账明细">
            <el-table-column prop="role_text" label="分账类型" width="130" />
            <el-table-column prop="name" label="接收方" min-width="140">
              <template slot-scope="scope">{{ scope.row.name || '-' }}</template>
            </el-table-column>
            <el-table-column label="接收方商户号" prop="account" min-width="160" show-overflow-tooltip />
            <el-table-column label="金额" width="110" align="right">
              <template slot-scope="scope">
                <span class="sharing-amount">+¥{{ Number(scope.row.amount).toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="备注" prop="description" min-width="180" show-overflow-tooltip />
          </el-table>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getTodayFlowOrders } from '@/api'

export default {
  name: 'Flow',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      limit: 10,
      keyword: '',
      detailVisible: false,
      detailLoading: false,
      currentOrder: null
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    fetchList() {
      this.loading = true
      getTodayFlowOrders({
        page: this.page,
        limit: this.limit,
        keyword: this.keyword || undefined
      }).then(res => {
        this.list = res.list || []
        this.total = res.total || 0
      }).catch(() => {
        this.$message.error('获取今日流水失败')
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    statusTag(status) {
      const map = { 0: 'warning', 1: '', 2: 'info', 3: 'success', 4: 'danger', 5: 'warning', 6: 'info' }
      return map[status] || 'info'
    },
    sharingStatusTag(status) {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger' }
      return map[status] || 'info'
    },
    goodsTypeTagType(goodsType) {
      const map = { 1: 'warning', 2: 'success', 3: 'info', 4: 'danger' }
      return map[goodsType] || 'info'
    },
    showDetail(row) {
      this.currentOrder = row
      this.detailVisible = true
    },
    goBack() {
      // 「返回」跳到第一个有权限的菜单：账号可能没有首页数据权限，固定跳 /dashboard 会被守卫拦下
      this.$router.push(this.$store.getters.firstAccessiblePath || '/403')
    }
  }
}
</script>

<style scoped>
.flow-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.flow-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}
.search-bar {
  margin-bottom: 16px;
}
.sub-text {
  font-size: 12px;
  color: #909399;
}
.amount-total {
  color: #e6a23c;
  font-weight: bold;
}
.sharing-list {
  line-height: 1.6;
}
.sharing-item {
  display: flex;
  justify-content: space-between;
}
.sharing-role {
  color: #606266;
}
.sharing-amount {
  color: #67c23a;
  font-weight: bold;
}
.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.detail-section {
  margin-bottom: 16px;
}
.detail-section-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin: 16px 0 12px;
  padding-left: 8px;
  border-left: 3px solid #3a8ee6;
}
</style>
